import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpClient } from '@angular/common/http';
import { Observable, from, throwError, BehaviorSubject } from 'rxjs';
import { switchMap, catchError, filter, take, finalize } from 'rxjs/operators';
import { EngineGateService } from '../engine-gate.service';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
    private token: string | null = null;
    private exp: number | null = null;

    private refreshingToken = false;
    public refreshTokenSubject = new BehaviorSubject<string | null>(null);

    // Array of URL patterns that should bypass authentication. The billing email-verification and
    // unsubscribe endpoints are public (reached from an email link logged-out) — attaching/refreshing
    // a token would fail and block the call, so they must skip the interceptor.
    private exemptUrls: string[] = [
        '/api/microsoft/oauth/session',
        '/api/microsoft/oauth/token',
        '/api/octiga/v3/billing/verify-email',
        '/api/octiga/v3/unsubscribe',
        // Cookie-authenticated and ahead of the engine gate: a token would need a mint, which the gate is refusing.
        '/api/microsoft/oauth/switch-back-request',
    ];

    constructor(
        private httpClient: HttpClient,
        private engineGate: EngineGateService,
    ) {}

    private isUrlExempt(url: string): boolean {
        return this.exemptUrls.some((exemptUrl) => url.includes(exemptUrl));
    }

    private isTokenExpired(): boolean {
        const currentTime = Math.floor(Date.now() / 1000);
        return !this.exp || this.exp < currentTime;
    }

    private handleRefreshToken(): Observable<string> {
        if (!this.refreshingToken) {
            this.refreshingToken = true;
            this.refreshTokenSubject.next(null);

            return from(this.httpClient.get<{ jwt: string; exp: number }>('/api/microsoft/oauth/token')).pipe(
                switchMap(({ jwt, exp }) => {
                    this.refreshingToken = false;
                    this.token = jwt;
                    this.exp = exp;
                    this.refreshTokenSubject.next(jwt);
                    return [jwt];
                }),
                catchError((error) => {
                    // Offer the failed mint to the engine gate (KRAK-434): it shows the "on the new version" /
                    // "being switched" page for the gate's answers and ignores every other error.
                    this.engineGate.observe(error);
                    this.refreshingToken = false;
                    this.token = null;
                    this.exp = null;
                    this.refreshTokenSubject.next(null);
                    return throwError(() => error);
                }),
                finalize(() => {
                    this.refreshingToken = false;
                }),
            );
        }

        return this.refreshTokenSubject.pipe(
            filter((token) => token !== null),
            take(1),
        );
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Check if URL is exempt from authentication
        if (this.isUrlExempt(request.url)) {
            return next.handle(request);
        }

        // Check if token is expired
        if (!this.token || this.isTokenExpired()) {
            return this.handleRefreshToken().pipe(
                switchMap((newToken) => {
                    const clonedRequest = request.clone({
                        setHeaders: {
                            Authorization: `Bearer ${newToken}`,
                        },
                    });
                    return next.handle(clonedRequest);
                }),
                catchError((error) => {
                    // Handle refresh failure (e.g., redirect to login)
                    return throwError(() => error);
                }),
            );
        }

        // Proceed with existing valid token
        const clonedRequest = request.clone({
            setHeaders: {
                Authorization: `Bearer ${this.token}`,
            },
        });
        return next.handle(clonedRequest);
    }
}
